import { useCallback, useState } from 'react';
import { SocialPlatform } from '../types';

interface UseComposerConnectProps {
  saveHandle: (platform: SocialPlatform, handle: string) => Promise<boolean>;
  /** Called with the platform that just got a handle, so it can be selected. */
  onConnected: (platform: SocialPlatform) => void;
}

/**
 * Connecting a channel without leaving the Composer.
 *
 * A chip with no handle opens the handle form under the chips row. Saving it
 * connects the channel and selects it, so the post the artist was already
 * writing gains a channel instead of losing the page.
 */
export function useComposerConnect({ saveHandle, onConnected }: UseComposerConnectProps) {
  const [connecting, setConnecting] = useState<SocialPlatform>();

  const open = useCallback((platform: SocialPlatform) => setConnecting(platform), []);
  const cancel = useCallback(() => setConnecting(undefined), []);

  const save = useCallback(
    (handle: string) => {
      if (connecting === undefined) {
        return;
      }
      const platform = connecting;
      saveHandle(platform, handle)
        .then((saved) => {
          if (saved) {
            setConnecting(undefined);
            onConnected(platform);
          }
          return saved;
        })
        .catch(console.error);
    },
    [connecting, onConnected, saveHandle]
  );

  return { connecting, open, cancel, save };
}
